import { useState, useEffect, useRef } from 'react';
import { buildImage } from '../utils/product';

interface TsneEntry {
  sku: string;
  quantity: number;
  url_img_list?: string;
  sketch_url?: string;
  grid_x: number;
  grid_y: number;
}

interface TsneInlineViewProps {
  entries: TsneEntry[];
  title: string;
  onBack: () => void;
  onProductClick?: (sku: string) => void;
  width?: number;
  height?: number;
  backgroundColor?: string;
}

export function TsneInlineView({ 
  entries, 
  title, 
  onBack,
  onProductClick,
  width = 600,
  height = 400,
  backgroundColor = '#f9fafb'
}: TsneInlineViewProps) {
  const [imageIndices, setImageIndices] = useState<{ [sku: string]: number }>({});
  const [hoveredSku, setHoveredSku] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const lastScrollTimeRef = useRef<{ [sku: string]: number }>({});

  // Calculate grid dimensions based on backend algorithm
  const GRID_ASPECT = 3 / 2; // width/height ratio from backend
  
  const N = entries.length;
  const cols = Math.ceil(Math.sqrt(N * GRID_ASPECT));
  const rows = Math.ceil(N / cols);

  const getImageUrls = (entry: TsneEntry): string[] => {
    const urls: (string | null)[] = [];
    
    // Handle URL_IMG_LIST as serialized string with {}
    if (entry.url_img_list && typeof entry.url_img_list === 'string' && entry.url_img_list.trim()) {
      const cleanUrls = entry.url_img_list.replace(/[{}]/g, '').split(',').map(u => u.trim()).filter(Boolean);
      urls.push(...cleanUrls.map(url => buildImage(url)));
    }
    
    // Add SKETCH_URL as fallback
    if (entry.sketch_url && typeof entry.sketch_url === 'string' && entry.sketch_url.trim()) {
      urls.push(buildImage(entry.sketch_url));
    }
    
    return urls.filter((url): url is string => Boolean(url));
  };

  const handleImageWheel = (e: React.WheelEvent, entry: TsneEntry, imageUrls: string[]) => {
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    const lastTime = lastScrollTimeRef.current[entry.sku] || 0;
    const THROTTLE_MS = 150;
    
    if (now - lastTime < THROTTLE_MS) {
      return;
    }
    
    lastScrollTimeRef.current[entry.sku] = now;
    
    const currentIndex = imageIndices[entry.sku] || 0;
    const delta = e.deltaY > 0 ? 1 : -1;
    const newIndex = (currentIndex + delta + imageUrls.length) % imageUrls.length;
    
    setImageIndices(prev => ({
      ...prev,
      [entry.sku]: newIndex
    }));
  };

  const handleImageTouch = (entry: TsneEntry, imageUrls: string[]) => {
    const currentIndex = imageIndices[entry.sku] || 0;
    const newIndex = (currentIndex + 1) % imageUrls.length;
    setImageIndices(prev => ({
      ...prev,
      [entry.sku]: newIndex
    }));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, sku: string) => {
    setHoveredSku(sku);
    setTooltipPosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredSku(null);
    setTooltipPosition(null);
  };

  // Calculate actual dimensions based on available space
  // Reserve space for header (~40px) and gap (8px)
  const headerHeight = 48;
  const availableWidth = Math.max(width, 300); // Minimum 300px
  const availableHeight = Math.max(height - headerHeight, 200); // Minimum 200px
  
  const baseImageSize = 88;
  const idealWidth = cols * baseImageSize;
  const idealHeight = rows * baseImageSize;
  
  const scaleX = availableWidth / idealWidth;
  const scaleY = availableHeight / idealHeight;
  const scale = Math.min(scaleX, scaleY, 1);
  
  const containerWidth = idealWidth * scale;
  const containerHeight = idealHeight * scale;

  // Get contrast color for text
  const getContrastColor = (hexColor: string): string => {
    if (!hexColor) return '#000000';
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  const textColor = getContrastColor(backgroundColor);

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
      <div className="flex flex-col gap-2 w-full" style={{ maxWidth: `${width}px` }}>
      {/* Header with back button */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          onClick={onBack}
          className="px-3 py-1 text-xs sm:text-sm font-semibold hover:opacity-80 transition-opacity whitespace-nowrap"
          style={{ 
            borderRadius: '0px',
            backgroundColor: backgroundColor,
            color: textColor,
            border: `2px solid ${textColor}20`,
            flexShrink: 0
          }}
        >
          ← Indietro
        </button>
        <span 
          className="text-xs sm:text-sm font-semibold uppercase tracking-wide px-2 sm:px-3 py-1 truncate"
          style={{ 
            color: textColor,
            backgroundColor: backgroundColor,
            border: `2px solid ${textColor}20`,
            maxWidth: 'calc(100% - 120px)'
          }}
        >
          {title} | Prodotti: {entries.length} | Capi: {entries.reduce((sum, e) => sum + e.quantity, 0)}
        </span>
      </div>

      {/* T-SNE Grid */}
      <div 
        className="relative overflow-hidden transition-colors duration-500"
        style={{ 
          width: `${Math.min(width, availableWidth)}px`, 
          height: `${availableHeight}px`,
          backgroundColor: backgroundColor,
          border: `3px solid ${textColor}20`,
          boxShadow: `inset 0 0 20px ${textColor}10`
        }}
      >
        <div
          className="absolute"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: `${containerWidth}px`,
            height: `${containerHeight}px`,
          }}
        >
          {entries.map((entry, index) => {
            const imageUrls = getImageUrls(entry);
            const currentIndex = imageIndices[entry.sku] || 0;
            const currentImageUrl = imageUrls[currentIndex] || null;
            
            // Calculate actual cell size based on container and grid dimensions
            const cellWidth = containerWidth / cols;
            const cellHeight = containerHeight / rows;
            const actualImageSize = Math.min(cellWidth, cellHeight) * 0.95; // 95% to add small gap
            
            // Position based on grid coordinates (0-1 range)
            const left = entry.grid_x * containerWidth - actualImageSize / 2;
            const top = entry.grid_y * containerHeight - actualImageSize / 2;

            return (
              <div
                key={`${entry.sku}-${index}`}
                className="absolute bg-white cursor-pointer shadow-sm hover:shadow-md transition-all duration-200"
                style={{
                  left: `${left}px`,
                  top: `${top}px`,
                  width: `${actualImageSize}px`,
                  height: `${actualImageSize}px`,
                  margin: 0,
                  padding: 0,
                  border: '1px solid #e5e7eb',
                  animation: 'fadeIn 0.3s ease-out forwards',
                  animationDelay: `${Math.min(index * 8, 800)}ms`,
                  opacity: 0
                }}
                onWheel={(e) => handleImageWheel(e, entry, imageUrls)}
                onClick={() => {
                  handleImageTouch(entry, imageUrls);
                  if (onProductClick) {
                    onProductClick(entry.sku);
                  }
                }}
                onMouseMove={(e) => handleMouseMove(e, entry.sku)}
                onMouseLeave={handleMouseLeave}
              >
                {currentImageUrl ? (
                  <img
                    src={currentImageUrl}
                    alt={entry.sku}
                    className="w-full h-full object-cover"
                    style={{ margin: 0, padding: 0, display: 'block' }}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs">
                    No img
                  </div>
                )}
                
                {/* Quantity badge */}
                {entry.quantity > 1 && (
                  <div
                    className="absolute bottom-0 right-0 bg-black text-white rounded-full flex items-center justify-center font-bold text-[10px]"
                    style={{
                      width: '20px',
                      height: '20px',
                      margin: '2px'
                    }}
                  >
                    {entry.quantity}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Tooltip */}
        {hoveredSku && tooltipPosition && (
          <div
            className="fixed bg-black text-white text-xs px-2 py-1 rounded pointer-events-none z-50"
            style={{
              left: `${tooltipPosition.x + 10}px`,
              top: `${tooltipPosition.y + 10}px`,
            }}
          >
            SKU: {hoveredSku}
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default TsneInlineView;

